// Authenticated-only client telemetry singleton.
//
// Queues events in memory, batches POSTs to /api/telemetry/events, and flushes
// on visibility change / pagehide so the last events before a tab close still
// land. We deliberately use `fetch(..., { keepalive: true })` rather than
// `navigator.sendBeacon` so we can attach the CSRF header — `sendBeacon` does
// not let callers set custom headers, and the server requires `X-CSRF-Token`
// on every /api/* mutation.
//
// SSR-safe: every `window` / `document` / `navigator` / `crypto` access is
// guarded so importing this module on the Worker side (or during a build pass)
// does not crash.

import type { TelemetryEvents } from '@wwaave/shared';

type TelemetryEventKind = TelemetryEvents.TelemetryEventKind;

type QueuedEvent = {
  kind: TelemetryEventKind;
  ts: number;
  sessionId: string;
  pagePath: string;
  props?: Record<string, unknown>;
  durationMs?: number;
  clientTsOffsetMs?: number;
};

const ENDPOINT = '/api/telemetry/events';
const MAX_QUEUE = 200;
const BATCH_SIZE = 20;
const FLUSH_CHUNK = 50;
const IDLE_MS = 5000;

class Telemetry {
  private queue: QueuedEvent[] = [];
  private readonly sessionId: string;
  private readonly sessionStartedAt: number;
  private user: { id: string } | null = null;
  private csrfToken: string | null = null;
  private flushTimer: number | null = null;
  private flushing = false;
  private visibilityBound = false;

  constructor() {
    this.sessionStartedAt = Date.now();
    this.sessionId = generateSessionId();
  }

  attachAuth(user: { id: string }, csrfToken: string | null): void {
    this.user = user;
    this.csrfToken = csrfToken;
    this.bindVisibilityFlush();
    this.track({
      kind: 'session.started',
      props: { sessionStartedAt: this.sessionStartedAt },
    });
  }

  detachAuth(): void {
    void this.flush();
    this.user = null;
    this.csrfToken = null;
  }

  track(input: {
    kind: TelemetryEventKind;
    props?: Record<string, unknown>;
    durationMs?: number;
  }): void {
    if (this.user === null) return;
    if (typeof navigator !== 'undefined' && navigator.doNotTrack === '1') return;
    if (this.queue.length >= MAX_QUEUE) return;

    const event: QueuedEvent = {
      kind: input.kind,
      ts: Date.now(),
      sessionId: this.sessionId,
      pagePath: typeof location !== 'undefined' ? location.pathname : '/',
      clientTsOffsetMs: Date.now() - this.sessionStartedAt,
    };
    if (input.props !== undefined) event.props = input.props;
    if (input.durationMs !== undefined) event.durationMs = input.durationMs;
    this.queue.push(event);

    if (this.queue.length >= BATCH_SIZE) {
      void this.flush();
    } else {
      this.scheduleFlush();
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    if (typeof window === 'undefined') return;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, IDLE_MS);
  }

  private async flush(): Promise<void> {
    if (this.flushing) return;
    if (this.queue.length === 0) return;
    if (this.user === null) return;
    if (typeof fetch === 'undefined') return;

    this.flushing = true;
    const batch = this.queue.splice(0, FLUSH_CHUNK);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.csrfToken !== null && this.csrfToken.length > 0) {
        headers['X-CSRF-Token'] = this.csrfToken;
      }
      await fetch(ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify({ events: batch }),
        credentials: 'include',
        keepalive: true,
      });
    } catch {
      // Fail-open: drop the batch rather than requeue. Requeuing risks
      // unbounded memory growth when the network is offline for a long stretch.
    } finally {
      this.flushing = false;
    }
  }

  private bindVisibilityFlush(): void {
    if (this.visibilityBound) return;
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    this.visibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void this.flush();
      }
    });
    window.addEventListener('pagehide', () => {
      void this.flush();
    });
  }
}

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `s_${crypto.randomUUID()}`;
  }
  return `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export const telemetry = new Telemetry();

export function track(
  kind: TelemetryEventKind,
  props?: Record<string, unknown>,
  durationMs?: number,
): void {
  const input: { kind: TelemetryEventKind; props?: Record<string, unknown>; durationMs?: number } =
    { kind };
  if (props !== undefined) input.props = props;
  if (durationMs !== undefined) input.durationMs = durationMs;
  telemetry.track(input);
}

export type { TelemetryEventKind };
